<template>
  <v-card
    height="600px"
    class="d-flex flex-column"
  >
    <v-card-title class="d-flex justify-space-between align-center">
      <span class="text-h5">{{
        $t("admin.productsDatabase.authorsDatabase.title")
      }}</span>
      <v-btn
        icon
        @click="$emit('close')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="flex-grow-0">
      <p>{{ $t("admin.productsDatabase.authorsDatabase.description") }}</p>

      <v-row>
        <v-col
          cols="12"
          md="8"
        >
          <v-text-field
            v-model="searchQuery"
            :label="
              $t('admin.productsDatabase.authorsDatabase.searchPlaceholder')
            "
            prepend-icon="mdi-magnify"
            outlined
            dense
            @input="debounceSearch"
          />
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text class="flex-grow-1 overflow-y-auto">
      <ekitabu-data-table
        :headers="headers"
        :items="authors"
        :loading="loading"
        :items-per-page="-1"
        hide-default-footer
      >
        <!-- Bio column with truncation -->
        <template #item.bio="{ item }">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <div
                v-bind="attrs"
                class="text-truncate"
                style="max-width: 300px"
                v-on="on"
              >
                {{
                  item.bio || $t("admin.productsDatabase.authorsDatabase.noBio")
                }}
              </div>
            </template>
            <span>{{
              item.bio || $t("admin.productsDatabase.authorsDatabase.noBio")
            }}</span>
          </v-tooltip>
        </template>

        <template #item.createdAt="{ item }">
          {{ new Date(item.createdAt).toLocaleDateString() }}
        </template>

        <template #item.action="{ item }">
          <v-btn
            small
            icon
            @click="openEditDialog(item)"
          >
            <v-icon>mdi-pencil</v-icon>
          </v-btn>
          <v-btn
            color="error"
            small
            icon
            @click="confirmDeleteAuthor(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
        </template>
      </ekitabu-data-table>

      <!-- Infinite scroll loading indicator -->
      <div
        v-if="loading && authors.length > 0"
        class="text-center my-2"
      >
        <v-progress-circular indeterminate />
      </div>

      <!-- Intersection observer target -->
      <div ref="loadMoreTrigger" />
    </v-card-text>

    <v-divider />

    <v-card-actions class="pa-4">
      <v-btn
        color="primary"
        large
        rounded
        @click="openCreateDialog"
      >
        {{ $t("admin.productsDatabase.authorsDatabase.addAuthor") }}
      </v-btn>
    </v-card-actions>

    <!-- Delete confirmation dialog -->
    <v-dialog
      v-model="deleteDialog"
      max-width="400px"
    >
      <v-card>
        <v-card-title>
          {{
            $t("admin.productsDatabase.authorsDatabase.confirmDelete")
          }}
        </v-card-title>
        <v-card-text>
          {{
            $t("admin.productsDatabase.authorsDatabase.confirmDeleteText", {
              name: authorToDelete?.name,
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="blue darken-1"
            text
            @click="deleteDialog = false"
          >
            {{ $t("common.cancel") }}
          </v-btn>
          <v-btn
            color="red darken-1"
            text
            @click="deleteAuthor"
          >
            {{ $t("common.remove") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AuthorProfileDialog
      v-model="profileDialog"
      :author="editingAuthor"
      :uploading-image="uploadingImage"
      :upload-progress="uploadProgress"
      @save="handleSave"
    />
  </v-card>
</template>

<script>
import axios from "axios";
import AuthorQueries from "@/graphql/AuthorQueries";
import infiniteScrollMixin from "@/mixins/infiniteScrollMixin";
import AuthorProfileDialog from "@/views/admin/ProductsDatabase/AuthorProfileDialog.vue";
import { removeIriId } from "@/iris";
import { AUTH_TOKEN } from "@/apollo/vue-apollo";

const apiBaseUrl = process.env.VUE_APP_API_HOST || "https://localhost";

export default {
  name: "AuthorsDatabase",
  components: { AuthorProfileDialog },
  mixins: [infiniteScrollMixin],
  emits: ["close"],

  data() {
    return {
      headers: [
        { text: this.$t("common.name"), value: "name" },
        {
          text: this.$t("admin.productsDatabase.authorsDatabase.bioHeader"),
          value: "bio",
        },
        {
          text: this.$t(
            "admin.productsDatabase.authorsDatabase.dateAddedHeader"
          ),
          value: "createdAt",
        },
        { text: "", value: "action", sortable: false, width: "100px" },
      ],
      deleteDialog: false,
      authorToDelete: null,
      profileDialog: false,
      editingAuthor: null,
      uploadingImage: false,
      uploadProgress: 0,
    };
  },

  computed: {
    authors() {
      return this.items; // Map the generic items from mixin to authors for readability
    },
  },

  methods: {
    async fetchItems() {
      this.loading = true;
      try {
        const result = await this.$apollo.query({
          query: AuthorQueries.getAuthors,
          variables: {
            first: this.fetchSize,
            after: this.pageInfo.endCursor,
            name: this.searchQuery || null,
          },
          fetchPolicy: "network-only",
        });

        const { edges, pageInfo, totalCount } = result.data.authors;

        if (edges && edges.length > 0) {
          this.items = [...this.items, ...edges.map((edge) => edge.node)];
        }

        // Update page info
        this.pageInfo = {
          hasNextPage: pageInfo.hasNextPage,
          endCursor: pageInfo.endCursor,
        };
      } catch (error) {
        console.error("Error fetching authors:", error);
      } finally {
        this.loading = false;
      }
    },

    backendToForm(author) {
      return {
        id: author.id,
        name: author.name || "",
        bio: author.bio || "",
        website: author.websiteUrl || "",
        youtube: author.youtubeHandle || "",
        facebook: author.facebookHandle || "",
        instagram: author.instagramHandle || "",
        tiktok: author.tiktokHandle || "",
        x: author.twitterHandle || "",
        imageUrl: author.imageUrl || "",
      };
    },

    formToBackend(payload) {
      const nullIfEmpty = (v) => (v && v.length ? v : null);
      return {
        name: payload.name,
        bio: payload.bio,
        websiteUrl: nullIfEmpty(payload.website),
        twitterHandle: nullIfEmpty(payload.x),
        instagramHandle: nullIfEmpty(payload.instagram),
        tiktokHandle: nullIfEmpty(payload.tiktok),
        facebookHandle: nullIfEmpty(payload.facebook),
        youtubeHandle: nullIfEmpty(payload.youtube),
      };
    },

    openCreateDialog() {
      this.editingAuthor = null;
      this.profileDialog = true;
    },

    openEditDialog(author) {
      this.editingAuthor = this.backendToForm(author);
      this.profileDialog = true;
    },

    async handleSave({ payload, mode }) {
      if (mode === "create") {
        await this.createAuthor(payload);
      } else {
        await this.updateAuthor(payload);
      }
    },

    async createAuthor(payload) {
      this.loading = true;
      let imageUploadFailed = false;
      try {
        const result = await this.$apollo.mutate({
          mutation: AuthorQueries.addAuthor,
          variables: this.formToBackend(payload),
        });

        let newAuthor = result.data.createAuthor.author;

        if (payload.profileImageFile) {
          this.uploadingImage = true;
          this.uploadProgress = 0;
          try {
            const uploadResult = await this.uploadAuthorImage(
              newAuthor._id,
              payload.profileImageFile
            );
            newAuthor = {
              ...newAuthor,
              imageUrl: uploadResult.mediaObject.filePath,
            };
          } catch (uploadError) {
            console.error("Error uploading author image:", uploadError);
            imageUploadFailed = true;
          } finally {
            this.uploadingImage = false;
            this.uploadProgress = 0;
          }
        }

        this.items = [newAuthor, ...this.items];
        this.profileDialog = false;

        if (imageUploadFailed) {
          this.showNotification(
            this.$t(
              "admin.productsDatabase.authorsDatabase.authorAddedImageFailed"
            ),
            "warning"
          );
        } else {
          this.showNotification(
            this.$t("admin.productsDatabase.authorsDatabase.authorAdded")
          );
        }
      } catch (error) {
        console.error("Error adding author:", error);
        this.showNotification(
          error.message || this.$t("errors.general"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },

    async updateAuthor(payload) {
      if (!this.editingAuthor || !this.editingAuthor.id) return;

      this.loading = true;
      let imageUploadFailed = false;
      try {
        const authorUuid = removeIriId(this.editingAuthor.id);

        const result = await this.$apollo.mutate({
          mutation: AuthorQueries.updateAuthor,
          variables: {
            id: this.editingAuthor.id,
            ...this.formToBackend(payload),
          },
        });

        let updated = result.data.updateAuthor.author;

        if (payload.profileImageFile) {
          this.uploadingImage = true;
          this.uploadProgress = 0;
          try {
            const uploadResult = await this.uploadAuthorImage(
              authorUuid,
              payload.profileImageFile
            );
            updated = {
              ...updated,
              imageUrl: uploadResult.mediaObject.filePath,
            };
          } catch (uploadError) {
            console.error("Error uploading author image:", uploadError);
            imageUploadFailed = true;
          } finally {
            this.uploadingImage = false;
            this.uploadProgress = 0;
          }
        }

        this.items = this.items.map((a) =>
          a.id === updated.id ? updated : a
        );
        this.profileDialog = false;

        if (imageUploadFailed) {
          this.showNotification(
            this.$t(
              "admin.productsDatabase.authorsDatabase.authorUpdatedImageFailed"
            ),
            "warning"
          );
        } else {
          this.showNotification(
            this.$t("admin.productsDatabase.authorsDatabase.authorUpdated")
          );
        }
      } catch (error) {
        console.error("Error updating author:", error);
        this.showNotification(
          error.message || this.$t("errors.general"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },

    async uploadAuthorImage(authorUuid, file) {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem(AUTH_TOKEN);
      const headers = {
        "Content-Type": "multipart/form-data",
      };
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await axios.post(
        `${apiBaseUrl}/authors/${authorUuid}/image`,
        formData,
        {
          headers,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
          },
        }
      );

      return response.data;
    },

    confirmDeleteAuthor(author) {
      this.authorToDelete = author;
      this.deleteDialog = true;
    },

    async deleteAuthor() {
      if (!this.authorToDelete) return;

      this.loading = true;
      try {
        await this.$apollo.mutate({
          mutation: AuthorQueries.deleteAuthor,
          variables: {
            input: { id: this.authorToDelete.id },
          },
        });

        // Remove the author from the list
        this.items = this.items.filter(
          (author) => author.id !== this.authorToDelete.id
        );

        // Close the dialog
        this.deleteDialog = false;
        this.authorToDelete = null;

        // Show success notification
        this.showNotification(
          this.$t("admin.productsDatabase.authorsDatabase.authorDeleted")
        );
      } catch (error) {
        console.error("Error deleting author:", error);
        this.showNotification(
          error.message || this.$t("errors.general"),
          "error"
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.v-data-table ::v-deep .v-data-table__wrapper {
  overflow-y: visible;
}
</style>
